import z from "zod";
import { MoneyScheme } from "./money";
import {
  BookkeepingAccountIdScheme,
  PrepStationIdScheme,
  ProductIdScheme,
  ProductVariantIdScheme,
} from "./ids";
import { ProductDBScheme, RestGetProduct } from "./product";
import { ProductVariantScheme } from "./productVariant";

export const ProductVariantOverlayScheme = z.object({
  unitPrice: MoneyScheme.nullable().optional(),
  takeawayPrice: MoneyScheme.nullable().optional(),
});

export const ProductOverlayDBScheme = z.object({
  unitPrice: MoneyScheme.optional(),
  takeawayPrice: MoneyScheme.nullable().optional(),
  name: z.string().optional(),
  prepStationId: PrepStationIdScheme.nullable().optional(),
  bookkeepingAccountId: BookkeepingAccountIdScheme.nullable().optional(),
  variants: z
    .record(ProductVariantIdScheme, ProductVariantOverlayScheme)
    .optional(),
  updatedAt: z.iso.datetime(),
});

export const ProductOverlayFieldScheme = ProductOverlayDBScheme.omit({
  variants: true,
  updatedAt: true,
}).keyof();

export const ProductVariantOverlayFieldScheme =
  ProductVariantOverlayScheme.keyof();

export const RestUpsertProductOverlay = ProductOverlayDBScheme.omit({
  updatedAt: true,
}).extend({
  resetFields: z.array(ProductOverlayFieldScheme).optional(),
  resetVariantFields: z
    .record(ProductVariantIdScheme, z.array(ProductVariantOverlayFieldScheme))
    .optional(),
});

export const RestProductOverlayChainValues = ProductDBScheme.pick({
  unitPrice: true,
  takeawayPrice: true,
  name: true,
  prepStationId: true,
  bookkeepingAccountId: true,
}).extend({
  variants: z
    .array(
      ProductVariantScheme.pick({
        id: true,
        unitPrice: true,
        takeawayPrice: true,
      }),
    )
    .optional(),
});

export const RestProductOverlayDetails = z.object({
  overlay: ProductOverlayDBScheme.nullable(),
  chainValues: RestProductOverlayChainValues,
});

export const RestProductOverlayListItem = RestProductOverlayDetails.extend({
  productId: ProductIdScheme,
  overlay: ProductOverlayDBScheme,
});

export const RestBulkUpsertProductOverlays = z.object({
  updates: z.array(
    z.object({
      productId: ProductIdScheme,
      overlay: RestUpsertProductOverlay,
    }),
  ),
});

export const RestBulkUpsertProductOverlaysResponse = z.object({
  updated: z.array(RestGetProduct),
  failed: z.array(
    z.object({
      productId: ProductIdScheme,
      error: z.string(),
    }),
  ),
});

export type DBProductOverlay = z.infer<typeof ProductOverlayDBScheme>;
export type ProductVariantOverlay = z.infer<typeof ProductVariantOverlayScheme>;
export type ProductOverlayField = z.infer<typeof ProductOverlayFieldScheme>;
export type ProductVariantOverlayField = z.infer<
  typeof ProductVariantOverlayFieldScheme
>;
export type RestUpsertProductOverlay = z.infer<typeof RestUpsertProductOverlay>;
export type RestProductOverlay = DBProductOverlay;
export type RestProductOverlayChainValues = z.infer<
  typeof RestProductOverlayChainValues
>;
export type RestProductOverlayDetails = z.infer<
  typeof RestProductOverlayDetails
>;
export type RestProductOverlayListItem = z.infer<
  typeof RestProductOverlayListItem
>;
export type RestBulkUpsertProductOverlays = z.infer<
  typeof RestBulkUpsertProductOverlays
>;
export type RestBulkUpsertProductOverlaysResponse = z.infer<
  typeof RestBulkUpsertProductOverlaysResponse
>;
